import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from "react";

import { getStoredState, updateStoredState } from "../utils/localStorage";
import { readInstitutionsByMantenedora } from "../services/mantenedora";
import { hasValidToken } from "../utils/cookies";

const MantenedoraContext = createContext();

// Modos de visualização
export const VIEW_MODES = {
  INDIVIDUAL: "individual",
  CONSOLIDATED: "consolidated"
};

export const MantenedoraProvider = ({ children }) => {
  const stored = getStoredState() || {};

  const [mantenedora, setMantenedora] = useState(stored.mantenedora || null);
  const [needsMantenedora, setNeedsMantenedora] = useState(stored.needsMantenedora ?? false);
  // null = não inicializado ainda (cache ausente); false = confirmado sem contrato; true = contratado
  const [isPlanContracted, setIsPlanContracted] = useState(stored.isPlanContracted ?? null);
  const [isPlanContractPending, setIsPlanContractPending] = useState(stored.isPlanContractPending ?? false);
  
  // Novo estado para modo de visualização (individual ou consolidado)
  const [viewMode, setViewMode] = useState(stored.viewMode || VIEW_MODES.INDIVIDUAL);
  
  // Lista de instituições da mantenedora
  const [institutions, setInstitutions] = useState(stored.institutions || []);
  const [institutionsLoading, setInstitutionsLoading] = useState(false);
  
  // Instituição selecionada no modo individual (quando há múltiplas)
  const [selectedInstitutionId, setSelectedInstitutionId] = useState(stored.selectedInstitutionId || null);

  // Ref para acessar selectedInstitutionId dentro de loadInstitutions sem torná-lo
  // dependência do useCallback — evita recriar o callback (e disparar o useEffect)
  // a cada troca de instituição, o que causava chamadas extras à API e um potencial
  // race condition onde a closure capturava o valor antigo de selectedInstitutionId.
  const selectedInstitutionIdRef = useRef(selectedInstitutionId);
  useEffect(() => {
    selectedInstitutionIdRef.current = selectedInstitutionId;
  }, [selectedInstitutionId]);

  // Carregar instituições da mantenedora
  // Aceita mantenedoraId opcional para uso imediato após login (antes do state atualizar)
  const loadInstitutions = useCallback(async (mantenedoraId = null) => {
    const idToUse = mantenedoraId || mantenedora?.id;
    if (!idToUse) return [];

    if (!hasValidToken()) return [];

    setInstitutionsLoading(true);
    try {
      const result = await readInstitutionsByMantenedora();
      const institutionsList = result?.institutions || [];
      setInstitutions(institutionsList);

      // Sanitiza estado persistido: se a instituição selecionada não está
      // mais entre as autorizadas (ex.: usuário de mantida que antes via
      // todas e agora vê só a própria), realinha com a primeira da lista.
      // Usa ref em vez de capturar selectedInstitutionId na closure para
      // sempre ler o valor mais atual sem recriar o callback.
      const currentSelectedId = selectedInstitutionIdRef.current;
      const stillValid = institutionsList.some(
        (inst) => inst.id === currentSelectedId
      );
      if (!stillValid && institutionsList.length > 0) {
        setSelectedInstitutionId(institutionsList[0].id);
      }

      // Com apenas uma instituição autorizada não faz sentido manter o
      // modo consolidado eventualmente persistido no localStorage.
      if (institutionsList.length <= 1) {
        setViewMode(VIEW_MODES.INDIVIDUAL);
      }

      return institutionsList;
    } catch (error) {
      console.error("Erro ao carregar instituições:", error);
      // Não limpa institutions em caso de erro para não esconder o seletor
      // de instituição do header enquanto o backend está indisponível.
      return [];
    } finally {
      setInstitutionsLoading(false);
    }
  }, [mantenedora?.id]);

  // Carregar instituições quando mantenedora muda
  useEffect(() => {
    if (mantenedora?.id) {
      loadInstitutions();
    }
  }, [mantenedora?.id, loadInstitutions]);

  // Persistir estado
  useEffect(() => {
    try {
      updateStoredState({
        mantenedora,
        needsMantenedora,
        isPlanContracted,
        isPlanContractPending,
        viewMode,
        institutions,
        selectedInstitutionId
      });
    } catch (error) {
      console.error("Erro ao salvar estado da mantenedora:", error);
    }
  }, [mantenedora, needsMantenedora, isPlanContracted, isPlanContractPending, viewMode, institutions, selectedInstitutionId]);

  const clearMantenedora = () => {
    setMantenedora(null);
    setNeedsMantenedora(false);
    setIsPlanContracted(false);
    setIsPlanContractPending(false);
    setViewMode(VIEW_MODES.INDIVIDUAL);
    setInstitutions([]);
    setSelectedInstitutionId(null);
    updateStoredState({
      mantenedora: null,
      needsMantenedora: false,
      isPlanContracted: false,
      isPlanContractPending: false,
      viewMode: VIEW_MODES.INDIVIDUAL,
      institutions: [],
      selectedInstitutionId: null
    });
  };

  // Alternar para modo consolidado.
  // Bloqueia a alternância quando o usuário tem acesso a apenas uma
  // instituição — nesse caso a "visão consolidada" é equivalente à individual
  // e habilitar o modo abriria espaço para inconsistências de UI.
  const setConsolidatedView = () => {
    if (institutions.length <= 1) {
      setViewMode(VIEW_MODES.INDIVIDUAL);
      return;
    }
    setViewMode(VIEW_MODES.CONSOLIDATED);
  };

  // Alternar para modo individual com uma instituição específica
  const setIndividualView = (institutionId = null) => {
    setViewMode(VIEW_MODES.INDIVIDUAL);
    if (institutionId) {
      setSelectedInstitutionId(institutionId);
    }
  };

  // Verificar se está no modo consolidado
  const isConsolidatedView = viewMode === VIEW_MODES.CONSOLIDATED;

  // Obter a instituição atual (para uso nos dashboards)
  const getCurrentInstitution = () => {
    if (isConsolidatedView) {
      return null; // No modo consolidado, não há instituição única
    }
    return institutions.find(inst => inst.id === selectedInstitutionId) || institutions[0] || null;
  };

  // Obter IDs de todas as instituições (para consultas consolidadas)
  const getAllInstitutionIds = () => {
    return institutions.map(inst => inst.id);
  };

  return (
    <MantenedoraContext.Provider
      value={{
        // Estado original
        mantenedora,
        setMantenedora,
        needsMantenedora,
        setNeedsMantenedora,
        isPlanContracted,
        setIsPlanContracted,
        isPlanContractPending,
        setIsPlanContractPending,
        clearMantenedora,
        
        // Novo estado para modo de visualização
        viewMode,
        setViewMode,
        isConsolidatedView,
        setConsolidatedView,
        setIndividualView,
        
        // Instituições
        institutions,
        setInstitutions,
        institutionsLoading,
        loadInstitutions,
        selectedInstitutionId,
        setSelectedInstitutionId,
        
        // Helpers
        getCurrentInstitution,
        getAllInstitutionIds,
        hasMultipleInstitutions: institutions.length > 1
      }}
    >
      {children}
    </MantenedoraContext.Provider>
  );
};

export const useMantenedora = () => useContext(MantenedoraContext);
